import React from 'react';
import _ from 'underscore';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {PROXIED_NOTE_PROP_TYPES, ProxiedNotePropTypes} from 'notebook/util/NoteUtilConstants';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayResult} from 'results/assays/AssayRecordConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import componentName from 'util/componentName';

import AssayDataNotePart from './assay-group/AssayDataNotePart';
import AssayResultsTable from './AssayResultsTable';
import {DisplayConfig} from './AttachmentConstants';
import EmptyAssayDataMessage from './EmptyAssayDataMessage';

function NoteLinkedAssayResults(props: NoteLinkedAssayResultsProps): JSX.Element {
  const isLoading = props.assayResultSchemas == null || props.assayResults == null;
  if (isLoading) {
    return (
      <div className="u-text-align-center">
        <HorizontalThrobber />
      </div>
    );
  }

  if (props.assayResults.nonArchived.length === 0) {
    const emptyDataTitle = `Attached ${MODEL_NAMES.Results}`;
    if (props.assayResults.archived.length === 0) {
      return (
        <EmptyAssayDataMessage indentation={props.indentation} title={emptyDataTitle}>
          <span>
            Insert {MODEL_NAMES.run} to see {MODEL_NAMES.results}
          </span>
        </EmptyAssayDataMessage>
      );
    }
    return (
      <EmptyAssayDataMessage indentation={props.indentation} title={emptyDataTitle}>
        <span>
          These {MODEL_NAMES.results} are archived. If the data was entered manually, it may have been removed
          from the entry.
        </span>
      </EmptyAssayDataMessage>
    );
  }

  const results = props.assayResults.nonArchived;

  assert(
    // eslint-disable-next-line no-restricted-syntax
    _.uniq(_.pluck(results, 'schemaId')).length === 1,
    'Expected assay results to belong to the same schema'
  );

  const schema = _.findWhere(props.assayResultSchemas, {id: results[0].schemaId});
  if (schema == null) {
    return (
      <div className="u-text-align-center">
        <HorizontalThrobber />
      </div>
    );
  }

  return (
    <AssayDataNotePart includeWrapper={false} indentation={props.indentation}>
      <AssayResultsTable
        schema={schema}
        results={results}
        canRename={true}
        displayConfig={props.displayConfigBySchemaId[schema.id]}
        updateDisplayConfigHandler={props.handleUpdateDisplayConfig}
        // eslint-disable-next-line no-restricted-syntax
        {..._.pick(props, Object.keys(PROXIED_NOTE_PROP_TYPES))}
        // Override indentation since this component handles padding
        indentation={0}
      />
    </AssayDataNotePart>
  );
}

interface NoteLinkedAssayResultsProps extends ProxiedNotePropTypes {
  assayResultSchemas: Array<AssaySchema>;
  assayResults: {
    archived: Array<AssayResult>;
    nonArchived: Array<AssayResult>;
  };
  displayConfigBySchemaId: {[schemaId: string]: DisplayConfig};
  handleUpdateDisplayConfig: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function byArchiveStatus(propName: string): (props: any) => (props: any) => JSX.Element {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function makeComponent(WrappedComponent: (pros: any) => JSX.Element): (props: any) => JSX.Element {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function Component(props: any): JSX.Element {
      if (props[propName] == null) {
        return <WrappedComponent {...props} />;
      }

      const [archived, nonArchived] = _.partition(props[propName], isArchived);
      const childProps = {
        ...props,
        [propName]: {archived, nonArchived},
      };

      return <WrappedComponent {...childProps} />;
    }
    Component.displayName = `ByArchiveStatus(${componentName(WrappedComponent)})`;

    return Component;
  }

  return makeComponent;
}

export default byArchiveStatus('assayResults')(NoteLinkedAssayResults);
